import React, { useState, useRef, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

import { useToast } from '../../../components/ui/Toast';
import {
  Upload, FileText, AlertCircle, CheckCircle, X,
  ChevronRight, ChevronLeft, Download, RefreshCw,
  Users, AlertTriangle, Check
} from 'lucide-react';

const STEPS = ['upload', 'mapping', 'preview', 'importing', 'done'];

const SYSTEM_COLUMNS = [
  { key: 'first_name', label: 'First Name', required: false },
  { key: 'last_name', label: 'Last Name', required: false },
  { key: 'email', label: 'Email', required: false },
  { key: 'phone', label: 'Phone', required: false },
  { key: 'company', label: 'Company', required: false },
  { key: 'job_title', label: 'Job Title', required: false },
  { key: 'source', label: 'Source', required: false },
  { key: 'lead_score', label: 'Lead Score', required: false },
  { key: '__skip__', label: '— Skip this column —', required: false },
];

const AUTO_MAP = {
  'first name': 'first_name', 'firstname': 'first_name', 'first': 'first_name',
  'last name': 'last_name', 'lastname': 'last_name', 'last': 'last_name',
  'email': 'email', 'email address': 'email', 'e-mail': 'email',
  'phone': 'phone', 'phone number': 'phone', 'mobile': 'phone', 'cell': 'phone',
  'company': 'company', 'organization': 'company', 'org': 'company', 'business': 'company',
  'job title': 'job_title', 'title': 'job_title', 'position': 'job_title', 'role': 'job_title',
  'source': 'source', 'lead source': 'source',
  'score': 'lead_score', 'lead score': 'lead_score', 'rating': 'lead_score',
};

const parseCSV = (text) => {
  const lines = text?.split(/\r?\n/)?.filter(l => l?.trim());
  if (lines?.length < 2) return { headers: [], rows: [] };

  const parseRow = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line?.length; i++) {
      const ch = line?.[i];
      if (ch === '"') {
        if (inQuotes && line?.[i + 1] === '"') { current += '"'; i++; }
        else inQuotes = !inQuotes;
      } else if (ch === ',' && !inQuotes) {
        result?.push(current?.trim());
        current = '';
      } else {
        current += ch;
      }
    }
    result?.push(current?.trim());
    return result;
  };

  const headers = parseRow(lines?.[0]);
  const rows = lines?.slice(1)?.map(l => {
    const cells = parseRow(l);
    const obj = {};
    headers?.forEach((h, i) => { obj[h] = cells?.[i] || ''; });
    return obj;
  });
  return { headers, rows };
};

const validateRow = (row, mapping) => {
  const errors = [];
  const emailKey = Object.keys(mapping)?.find(k => mapping?.[k] === 'email');
  const firstNameKey = Object.keys(mapping)?.find(k => mapping?.[k] === 'first_name');
  const hasIdentifier = (emailKey && row?.[emailKey]?.trim()) || (firstNameKey && row?.[firstNameKey]?.trim());
  if (!hasIdentifier) errors?.push('Must have at least a name or email');

  if (emailKey && row?.[emailKey]?.trim()) {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(row?.[emailKey]?.trim())) {
      errors?.push('Invalid email format');
    }
  }
  const scoreKey = Object.keys(mapping)?.find(k => mapping?.[k] === 'lead_score');
  if (scoreKey && row?.[scoreKey]?.trim()) {
    const n = parseInt(row?.[scoreKey]);
    if (isNaN(n) || n < 0 || n > 100) errors?.push('Lead score must be 0–100');
  }
  return errors;
};

const BulkUploadModal = ({ isOpen, onClose, onImportComplete }) => {
  const { user } = useAuth();
  const toast = useToast();
  const fileInputRef = useRef(null);

  const [step, setStep] = useState('upload');
  const [isDragging, setIsDragging] = useState(false);
  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [validatedRows, setValidatedRows] = useState([]);
  const [importProgress, setImportProgress] = useState(0);
  const [importResults, setImportResults] = useState({ success: 0, failed: 0, errors: [] });
  const [parseError, setParseError] = useState('');

  const resetState = () => {
    setStep('upload');
    setIsDragging(false);
    setFileName('');
    setHeaders([]);
    setRows([]);
    setMapping({});
    setValidatedRows([]);
    setImportProgress(0);
    setImportResults({ success: 0, failed: 0, errors: [] });
    setParseError('');
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const processFile = (file) => {
    setParseError('');
    if (!file) return;
    if (!file?.name?.endsWith('.csv') && file?.type !== 'text/csv') {
      setParseError('Please upload a CSV file (.csv)');
      return;
    }
    if (file?.size > 5 * 1024 * 1024) {
      setParseError('File size must be under 5MB');
      return;
    }
    setFileName(file?.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e?.target?.result;
      const { headers: h, rows: r } = parseCSV(text);
      if (h?.length === 0) {
        setParseError('Could not parse CSV. Ensure the file has headers in the first row.');
        return;
      }
      if (r?.length === 0) {
        setParseError('CSV file has no data rows.');
        return;
      }
      if (r?.length > 1000) {
        setParseError('Maximum 1000 rows per upload. Please split your file.');
        return;
      }
      setHeaders(h);
      setRows(r);
      // Auto-map columns
      const autoMapping = {};
      h?.forEach(header => {
        const normalized = header?.toLowerCase()?.trim();
        autoMapping[header] = AUTO_MAP?.[normalized] || '__skip__';
      });
      setMapping(autoMapping);
      setStep('mapping');
    };
    reader.onerror = () => setParseError('Failed to read file.');
    reader?.readAsText(file);
  };

  const handleFileChange = (e) => {
    const file = e?.target?.files?.[0];
    if (file) processFile(file);
  };

  const handleDrop = useCallback((e) => {
    e?.preventDefault();
    setIsDragging(false);
    const file = e?.dataTransfer?.files?.[0];
    if (file) processFile(file);
  }, []);

  const handleDragOver = (e) => { e?.preventDefault(); setIsDragging(true); };
  const handleDragLeave = () => setIsDragging(false);

  const handleMappingChange = (header, value) => {
    setMapping(prev => ({ ...prev, [header]: value }));
  };

  const proceedToPreview = () => {
    const validated = rows?.map((row, idx) => {
      const errors = validateRow(row, mapping);
      return { ...row, __idx__: idx + 1, __errors__: errors, __valid__: errors?.length === 0 };
    });
    setValidatedRows(validated);
    setStep('preview');
  };

  const getMappedValue = (row, targetKey) => {
    const sourceHeader = Object.keys(mapping)?.find(h => mapping?.[h] === targetKey);
    return sourceHeader ? row?.[sourceHeader]?.trim() || '' : '';
  };

  const startImport = async () => {
    setStep('importing');
    setImportProgress(0);
    const validRows = validatedRows?.filter(r => r?.__valid__);
    let success = 0;
    let failed = 0;
    const errors = [];

    // Ensure user profile exists first
    try {
      await supabase?.rpc('ensure_user_profile_exists', {
        p_user_id: user?.id,
        p_email: user?.email || ''
      });
    } catch (e) {
      console.warn('ensure_user_profile_exists:', e);
    }

    // Get or create a default funnel for bulk imports
    let defaultFunnelId = null;
    try {
      const { data: existingFunnel } = await supabase
        ?.from('sales_funnels')
        ?.select('id')
        ?.eq('user_id', user?.id)
        ?.limit(1)
        ?.maybeSingle();
      if (existingFunnel?.id) {
        defaultFunnelId = existingFunnel?.id;
      } else {
        const { data: newFunnel } = await supabase
          ?.from('sales_funnels')
          ?.insert([{
            user_id: user?.id,
            name: 'Default Import Funnel',
            description: 'Auto-created for bulk imports',
            status: 'active',
            created_at: new Date()?.toISOString(),
            updated_at: new Date()?.toISOString(),
          }])
          ?.select('id')
          ?.single();
        defaultFunnelId = newFunnel?.id || null;
      }
    } catch (e) {
      console.warn('Could not get/create default funnel:', e);
    }

    // Batch insert in chunks of 50
    const CHUNK = 50;
    for (let i = 0; i < validRows?.length; i += CHUNK) {
      const chunk = validRows?.slice(i, i + CHUNK);
      const records = chunk?.map(row => {
        const record = {
          user_id: user?.id,
          first_name: getMappedValue(row, 'first_name'),
          last_name: getMappedValue(row, 'last_name'),
          email: getMappedValue(row, 'email'),
          phone: getMappedValue(row, 'phone'),
          company: getMappedValue(row, 'company'),
          job_title: getMappedValue(row, 'job_title'),
          source: getMappedValue(row, 'source') || 'csv_import',
          lead_score: parseInt(getMappedValue(row, 'lead_score')) || 50,
          is_converted: false,
          created_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString(),
        };
        if (defaultFunnelId) record.funnel_id = defaultFunnelId;
        return record;
      });

      const { error: insertError } = await supabase
        ?.from('funnel_leads')
        ?.insert(records);

      if (insertError) {
        failed += chunk?.length;
        errors?.push(`Rows ${i + 1}–${i + chunk?.length}: ${insertError?.message}`);
      } else {
        success += chunk?.length;
      }

      setImportProgress(Math.round(((i + CHUNK) / validRows?.length) * 100));
    }

    setImportResults({ success, failed, errors });
    setStep('done');

    if (success > 0) {
      toast?.success(`Successfully imported ${success} lead${success !== 1 ? 's' : ''}!`, 'Import Complete');
      if (onImportComplete) onImportComplete();
    }
  };

  const downloadTemplate = () => {
    const csv = 'first_name,last_name,email,phone,company,job_title,source,lead_score\nJohn,Doe,john@example.com,+1-555-0100,Acme Corp,CEO,website,75\nJane,Smith,jane@example.com,+1-555-0101,Beta Inc,VP Sales,referral,85';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'leads_template.csv';
    a?.click();
    URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  const validCount = validatedRows?.filter(r => r?.__valid__)?.length;
  const invalidCount = validatedRows?.filter(r => !r?.__valid__)?.length;
  const stepIndex = STEPS?.indexOf(step);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-xl shadow-elevation-3 w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Upload className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Bulk Upload Leads</h2>
              <p className="text-sm text-muted-foreground">Import multiple leads from a CSV file</p>
            </div>
          </div>
          <button onClick={handleClose} className="p-2 hover:bg-muted rounded-lg transition-colors">
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        {/* Step Indicator */}
        {step !== 'importing' && step !== 'done' && (
          <div className="px-6 pt-4 flex-shrink-0">
            <div className="flex items-center space-x-2">
              {['Upload File', 'Map Columns', 'Preview & Validate']?.map((label, i) => (
                <React.Fragment key={label}>
                  <div className="flex items-center space-x-1.5">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      i < stepIndex ? 'bg-green-500 text-white' :
                      i === stepIndex ? 'bg-primary text-primary-foreground': 'bg-muted text-muted-foreground'
                    }`}>
                      {i < stepIndex ? <Check className="w-3 h-3" /> : i + 1}
                    </div>
                    <span className={`text-xs font-medium ${
                      i === stepIndex ? 'text-foreground' : 'text-muted-foreground'
                    }`}>{label}</span>
                  </div>
                  {i < 2 && <div className="flex-1 h-px bg-border" />}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">

          {/* STEP 1: Upload */}
          {step === 'upload' && (
            <div className="space-y-4">
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => fileInputRef?.current?.click()}
                className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors ${
                  isDragging ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50 hover:bg-muted/30'
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={handleFileChange}
                />
                <div className="flex flex-col items-center space-y-3">
                  <div className={`p-4 rounded-full ${
                    isDragging ? 'bg-primary/10' : 'bg-muted'
                  }`}>
                    <Upload className={`w-8 h-8 ${
                      isDragging ? 'text-primary' : 'text-muted-foreground'
                    }`} />
                  </div>
                  <div>
                    <p className="text-base font-medium text-foreground">
                      {isDragging ? 'Drop your CSV file here' : 'Drag & drop your CSV file'}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">or click to browse — max 1,000 rows, 5MB</p>
                  </div>
                  <span className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium">
                    Choose File
                  </span>
                </div>
              </div>

              {parseError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center space-x-2">
                  <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                  <p className="text-red-700 text-sm">{parseError}</p>
                </div>
              )}

              <div className="bg-muted/50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-foreground">Need a template?</p>
                  <button
                    onClick={downloadTemplate}
                    className="flex items-center space-x-1.5 text-sm text-primary hover:underline"
                  >
                    <Download className="w-4 h-4" />
                    <span>Download CSV Template</span>
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Supported columns: first_name, last_name, email, phone, company, job_title, source, lead_score
                </p>
              </div>
            </div>
          )}

          {/* STEP 2: Column Mapping */}
          {step === 'mapping' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-foreground">Map your CSV columns to lead fields</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    File: <span className="font-medium">{fileName}</span> — {rows?.length} rows detected
                  </p>
                </div>
              </div>

              <div className="border border-border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">CSV Column</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Sample Data</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Maps To</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {headers?.map(header => (
                      <tr key={header} className="hover:bg-muted/20">
                        <td className="px-4 py-3">
                          <div className="flex items-center space-x-2">
                            <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                            <span className="font-medium text-foreground">{header}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-muted-foreground text-xs truncate max-w-[120px] block">
                            {rows?.[0]?.[header] || '—'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <select
                            value={mapping?.[header] || '__skip__'}
                            onChange={(e) => handleMappingChange(header, e?.target?.value)}
                            className="w-full text-sm border border-border rounded-lg px-3 py-1.5 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                          >
                            {SYSTEM_COLUMNS?.map(col => (
                              <option key={col?.key} value={col?.key}>{col?.label}</option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start space-x-2">
                <AlertCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                <p className="text-blue-700 text-xs">
                  Each lead must have at least a <strong>First Name</strong> or <strong>Email</strong> mapped. Columns set to "Skip" will be ignored.
                </p>
              </div>
            </div>
          )}

          {/* STEP 3: Preview & Validate */}
          {step === 'preview' && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-muted/50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-foreground">{validatedRows?.length}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Total Rows</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-green-600">{validCount}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Valid Leads</p>
                </div>
                <div className="bg-red-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-red-600">{invalidCount}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Invalid Rows</p>
                </div>
              </div>

              {invalidCount > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start space-x-2">
                  <AlertTriangle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <p className="text-yellow-700 text-xs">
                    {invalidCount} row{invalidCount !== 1 ? 's' : ''} will be skipped due to validation errors. Only valid rows will be imported.
                  </p>
                </div>
              )}

              <div className="border border-border rounded-lg overflow-hidden">
                <div className="bg-muted/50 px-4 py-2 flex items-center justify-between">
                  <span className="text-xs font-medium text-muted-foreground uppercase">Preview (first 10 rows)</span>
                  <span className="text-xs text-muted-foreground">{validatedRows?.length} total</span>
                </div>
                <div className="overflow-x-auto max-h-64">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/30 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left text-muted-foreground">#</th>
                        <th className="px-3 py-2 text-left text-muted-foreground">Status</th>
                        <th className="px-3 py-2 text-left text-muted-foreground">Name</th>
                        <th className="px-3 py-2 text-left text-muted-foreground">Email</th>
                        <th className="px-3 py-2 text-left text-muted-foreground">Company</th>
                        <th className="px-3 py-2 text-left text-muted-foreground">Phone</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {validatedRows?.slice(0, 10)?.map((row) => (
                        <tr key={row?.__idx__} className={row?.__valid__ ? 'hover:bg-muted/20' : 'bg-red-50/50'}>
                          <td className="px-3 py-2 text-muted-foreground">{row?.__idx__}</td>
                          <td className="px-3 py-2">
                            {row?.__valid__ ? (
                              <span className="inline-flex items-center space-x-1 text-green-600">
                                <CheckCircle className="w-3 h-3" />
                                <span>Valid</span>
                              </span>
                            ) : (
                              <span className="inline-flex items-center space-x-1 text-red-600" title={row?.__errors__?.join(', ')}>
                                <AlertCircle className="w-3 h-3" />
                                <span>Error</span>
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 font-medium text-foreground">
                            {[getMappedValue(row, 'first_name'), getMappedValue(row, 'last_name')]?.filter(Boolean)?.join(' ') || '—'}
                          </td>
                          <td className="px-3 py-2 text-muted-foreground">{getMappedValue(row, 'email') || '—'}</td>
                          <td className="px-3 py-2 text-muted-foreground">{getMappedValue(row, 'company') || '—'}</td>
                          <td className="px-3 py-2 text-muted-foreground">{getMappedValue(row, 'phone') || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {validatedRows?.length > 10 && (
                  <div className="px-4 py-2 bg-muted/20 border-t border-border">
                    <p className="text-xs text-muted-foreground">+ {validatedRows?.length - 10} more rows not shown</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* STEP 4: Importing */}
          {step === 'importing' && (
            <div className="flex flex-col items-center justify-center py-12 space-y-6">
              <div className="relative">
                <div className="w-20 h-20 rounded-full border-4 border-primary/20 flex items-center justify-center">
                  <RefreshCw className="w-8 h-8 text-primary animate-spin" />
                </div>
              </div>
              <div className="text-center">
                <p className="text-lg font-semibold text-foreground">Importing Leads...</p>
                <p className="text-sm text-muted-foreground mt-1">Please don't close this window</p>
              </div>
              <div className="w-full max-w-sm">
                <div className="flex justify-between text-xs text-muted-foreground mb-1">
                  <span>Progress</span>
                  <span>{Math.min(importProgress, 100)}%</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className="bg-primary h-2 rounded-full transition-all duration-300"
                    style={{ width: `${Math.min(importProgress, 100)}%` }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* STEP 5: Done */}
          {step === 'done' && (
            <div className="flex flex-col items-center justify-center py-8 space-y-5">
              <div className={`w-20 h-20 rounded-full flex items-center justify-center ${
                importResults?.success > 0 ? 'bg-green-100' : 'bg-red-100'
              }`}>
                {importResults?.success > 0 ? (
                  <CheckCircle className="w-10 h-10 text-green-500" />
                ) : (
                  <AlertCircle className="w-10 h-10 text-red-500" />
                )}
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-foreground">
                  {importResults?.success > 0 ? 'Import Complete!' : 'Import Failed'}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  {importResults?.success > 0
                    ? `${importResults?.success} lead${importResults?.success !== 1 ? 's' : ''} imported successfully`
                    : 'No leads were imported'}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4 w-full max-w-xs">
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-green-600">{importResults?.success}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Imported</p>
                </div>
                <div className="bg-red-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-red-600">{importResults?.failed}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Failed</p>
                </div>
              </div>
              {importResults?.errors?.length > 0 && (
                <div className="w-full bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-xs font-medium text-red-700 mb-1">Errors:</p>
                  {importResults?.errors?.map((err, i) => (
                    <p key={i} className="text-xs text-red-600">• {err}</p>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border flex-shrink-0">
          <div>
            {step === 'mapping' && (
              <button
                onClick={() => setStep('upload')}
                className="flex items-center space-x-1.5 px-4 py-2 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-muted transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
                <span>Back</span>
              </button>
            )}
            {step === 'preview' && (
              <button
                onClick={() => setStep('mapping')}
                className="flex items-center space-x-1.5 px-4 py-2 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-muted transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
                <span>Back</span>
              </button>
            )}
          </div>
          <div className="flex items-center space-x-3">
            {step !== 'importing' && (
              <button
                onClick={handleClose}
                className="px-4 py-2 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-muted transition-colors"
              >
                {step === 'done' ? 'Close' : 'Cancel'}
              </button>
            )}
            {step === 'mapping' && (
              <button
                onClick={proceedToPreview}
                className="flex items-center space-x-1.5 px-5 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                <span>Preview & Validate</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
            {step === 'preview' && (
              <button
                onClick={startImport}
                disabled={validCount === 0}
                className="flex items-center space-x-1.5 px-5 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Users className="w-4 h-4" />
                <span>Import {validCount} Lead{validCount !== 1 ? 's' : ''}</span>
              </button>
            )}
            {step === 'done' && importResults?.success === 0 && (
              <button
                onClick={resetState}
                className="flex items-center space-x-1.5 px-5 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Try Again</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkUploadModal;
